import {searchToolGuides} from './search-tool-guides.mjs';
// Tool specifications and usage guidance; previews are not measured customer results.

export const curatedToolDetails = {
  'website-seo-score-checker': {
    badge: 'Public Page SEO Audit',mockupType: 'scanner',
    previewMetrics: [{label:'Input',val:'Public page URL'},{label:'Processing',val:'Server-side public checks'},{label:'Result',val:'Run a public check',highlight:true}],
    pros:['Reports checks from the fetched page rather than a fixed score','Shows reasons and suggested fixes for supported checks','Available without creating an account'],
    cons:['Requires a publicly accessible HTML page','Does not execute remote JavaScript or confirm Google indexing','Provider availability and request limits can affect results'],
    howToUse:[
      {step:1,title:'Enter a public page URL',desc:'Use the final HTTPS address of the page you want to review. Do not submit private or internal URLs.'},
      {step:2,title:'Run the analysis',desc:'The public URL is sent to BizAIPro for fetching and supported checks. Wait for the report or an explicit error.'},
      {step:3,title:'Review the evidence',desc:'Read the observed metadata, check details and suggested fixes. A pass is limited to the stated check.'},
      {step:4,title:'Fix and verify',desc:'Apply relevant changes, rerun the page and track search results separately in Search Console.'}
    ],
    useCases:['Review titles and descriptions on an important landing page','Check crawl directives and canonical destinations before publication','Prioritize supported technical fixes for a client page'],
    faqs:[
      {q:'Is my URL processed in the browser?',a:'No. The public URL is sent to BizAIPro for analysis. The report may use public DNS and PageSpeed providers. Never submit credentials or private/internal URLs.'},
      {q:'Does a high SEO score guarantee rankings?',a:'No. The score summarizes supported configuration checks. It does not establish Google indexing, search position, traffic or a complete website audit.'}
    ]
  },
  'roas-calculator': {
    badge: 'Flagship Calculator',
    users: '14,200+ monthly users',
    rating: 4.9,
    mockupType: 'calculator',
    previewMetrics: [
      { label: 'Attributed Revenue', val: '$12,450.00' },
      { label: 'Ad Spend', val: '$3,112.50' },
      { label: 'ROAS Multiple', val: '4.00x (400%)', highlight: true }
    ],
    pros: [
      'Instant real-time calculation as you type with zero page reloads',
      'Calculates exact multiple (e.g. 4.0x) and percentage return (400%) simultaneously',
      '100% private in-browser math: financial numbers are never logged or stored on external servers',
      'Clear formula breakdown shows attributed revenue divided by total advertising expense'
    ],
    cons: [
      'Measures gross advertising revenue return, not net bottom-line profit',
      'Does not factor in variable product cost (COGS), refunds, or merchant transaction fees on its own (use Break-Even ROAS for margin analysis)',
      'Requires accurate attribution data from your advertising channels (Google Ads, Meta Ads)'
    ],
    howToUse: [
      { step: 1, title: 'Enter Ad Spend', desc: 'Enter advertising spend for the selected campaign and reporting period in the first field.' },
      { step: 2, title: 'Enter Attributed Revenue', desc: 'Enter revenue attributed to those ads in the second field, using the same currency and a documented attribution window.' },
      { step: 3, title: 'Review Return Multiple & Ratio', desc: 'Observe the instantaneous ROAS multiple (e.g. 3.5x). A ROAS above your break-even threshold means your campaigns are generating positive gross revenue.' },
      { step: 4, title: 'Compare Across Campaigns', desc: 'Benchmark channels against one another to identify top-performing ad creatives and allocate budget toward your highest-yield campaigns.' }
    ],
    useCases: [
      'E-commerce brand media buyers scaling Meta & TikTok ad spend profitably',
      'Digital marketing agencies reporting weekly campaign performance metrics to clients',
      'Small business owners assessing whether pay-per-click ads justify their operating cost'
    ],
    faqs: [
      { q: 'What is a good ROAS for small business e-commerce?', a: 'There is no universal benchmark. Compare revenue return with your contribution margin, fixed overhead and attribution assumptions. Use the break-even ROAS calculator to estimate a threshold before setting a planning target.' },
      { q: 'Is ROAS the same as ROI?', a: 'No. ROAS measures gross revenue generated per ad dollar. ROI measures net profit after deducting all expenses (product cost, fulfillment, overhead, taxes).' }
    ]
  },
  'break-even-roas-calculator': {
    badge: 'Margin Safeguard',
    users: '11,800+ monthly users',
    rating: 4.9,
    mockupType: 'calculator',
    previewMetrics: [
      { label: 'Selling Price', val: '$100.00' },
      { label: 'COGS & Variable Cost', val: '$45.00' },
      { label: 'Break-Even ROAS', val: '1.82x (182%)', highlight: true }
    ],
    pros: [
      'Prevents scaling unprofitable ad campaigns by pinpointing your exact survival threshold',
      'Incorporates product manufacturing cost (COGS), shipping, and payment processing fees',
      'Enables data-driven bidding strategies in Google Ads Smart Bidding and Meta Advantage+',
      'Zero subscription or sign-up required — instant calculation directly in your browser'
    ],
    cons: [
      'Focuses on per-unit contribution margins rather than fixed annual company overhead',
      'Assumes stable unit product costs and consistent return rates across customer cohorts',
      'Must keep all values in the same currency without double-counting fulfillment fees'
    ],
    howToUse: [
      { step: 1, title: 'Input Average Selling Price', desc: 'Enter the selling price or average order value after the discounts included in your model, using a consistent basis for revenue and costs.' },
      { step: 2, title: 'Add Product Unit Cost (COGS)', desc: 'Enter what you pay your supplier or manufacturer to produce the product.' },
      { step: 3, title: 'Factor Other Variable Costs', desc: 'Include shipping postage, packaging materials, and merchant credit card processing fees (e.g. 2.9% + 30¢).' },
      { step: 4, title: 'Review the Contribution Threshold', desc: 'This threshold covers only the variable costs entered and advertising. Account for overhead, refunds and attribution uncertainty before deciding on a target; no target guarantees profit.' }
    ],
    useCases: [
      'Shopify and WooCommerce store founders setting target ROAS guardrails',
      'Direct-to-consumer brands testing new product launch pricing models',
      'PPC media buyers auditing client margins before ramping up ad budgets'
    ],
    faqs: [
      { q: 'What happens if my campaign ROAS is below break-even?', a: 'If your actual campaign ROAS falls below this threshold, every sale generated by ads is actively losing money for your business once product and shipping costs are deducted.' }
    ]
  },
  'utm-builder': {
    badge: 'Marketing Standard',
    users: '21,500+ monthly users',
    rating: 4.9,
    mockupType: 'generator',
    previewMetrics: [
      { label: 'Destination URL', val: 'https://bizaipro.com/blog' },
      { label: 'Traffic Source', val: 'newsletter' },
      { label: 'Clean Tracking Link', val: '?utm_source=newsletter&utm_medium=email', highlight: true }
    ],
    pros: [
      'Standardizes campaign parameters to prevent messy, fragmented Google Analytics 4 reports',
      'Automatic lowercase formatting and space-to-dash conversion eliminates tracking discrepancies',
      'Supports all 5 core UTM tags: source, medium, campaign, term, and content',
      'One-click instant copy to clipboard with real-time URL syntax validation'
    ],
    cons: [
      'Visible in user browser address bars (use an affiliate cloaker or URL shortener if hiding tags is required)',
      'Can be stripped if a destination URL undergoes an unhandled 301/302 HTTP redirect',
      'Requires team discipline to use consistent naming conventions across all marketing channels'
    ],
    howToUse: [
      { step: 1, title: 'Paste Your Landing Page URL', desc: 'Enter the exact page address where you want visitors to arrive (e.g. https://yoursite.com/offer).' },
      { step: 2, title: 'Specify Traffic Source & Medium', desc: 'Enter where the link is placed (e.g. facebook, newsletter, twitter) and the medium (e.g. cpc, email, banner).' },
      { step: 3, title: 'Name Your Campaign', desc: 'Give the campaign a descriptive name (e.g. summer-sale-2026 or welcome-series).' },
      { step: 4, title: 'Copy & Deploy Link', desc: 'Click Copy to get the clean, verified tracking link ready to paste in your email or ad creatives.' }
    ],
    useCases: [
      'Email marketing managers tracking newsletter click-through performance in GA4',
      'Influencer campaign coordinators assigning dedicated tracking URLs to creators',
      'Content marketers identifying which blog links drive software trial registrations'
    ],
    faqs: [
      { q: 'Why is utm_medium different from utm_source?', a: 'Source identifies the specific platform (e.g. google, linkedin, beehiiv). Medium identifies the marketing channel type (e.g. cpc, organic, email, referral).' }
    ]
  },
  'website-health-checker': {
    badge: 'Security & SEO',
    users: '18,400+ monthly users',
    rating: 4.8,
    mockupType: 'scanner',
    previewMetrics: [
      { label: 'Target Host', val: 'bizaipro.com' },
      { label: 'HTTPS Protocol', val: 'TLS 1.3 Active' },
      { label: 'Health Score', val: '98 / 100 Grade A', highlight: true }
    ],
    pros: [
      'Comprehensive public audit of HTTPS configuration, TLS status, and security headers',
      'Checks critical SEO directives: robots.txt availability, canonical tags, and OpenGraph metadata',
      'Zero server agent installation: runs externally via standard secure web requests',
      'Provides actionable remediation steps for missing Content-Security-Policy and HSTS headers'
    ],
    cons: [
      'Inspects public HTML and HTTP responses; cannot scan password-protected admin areas or intranet pages',
      'Websites blocking external scanners via Cloudflare "Under Attack Mode" may require IP whitelisting'
    ],
    howToUse: [
      { step: 1, title: 'Enter Website Domain', desc: 'Type in your root domain or specific webpage URL (e.g. https://yourbusiness.com).' },
      { step: 2, title: 'Initiate Public Health Audit', desc: 'Click Run Audit to perform concurrent checks on DNS, SSL, response headers, and page structure.' },
      { step: 3, title: 'Review Security & SEO Findings', desc: 'Inspect individual pass/fail badges for SSL encryption, HSTS, X-Frame-Options, and meta viewport tags.' },
      { step: 4, title: 'Implement Quick Recommendations', desc: 'Copy suggested header configurations to your Cloudflare dashboard, Nginx, or WordPress settings.' }
    ],
    useCases: [
      'Web agencies onboarding new client websites to identify critical configuration flaws',
      'Security-conscious website owners verifying HTTPS and modern TLS 1.3 encryption',
      'SEO consultants ensuring robots.txt and canonical tags are properly indexed by search engines'
    ],
    faqs: [
      { q: 'Will scanning slow down my website?', a: 'No. The health checker issues single, lightweight HEAD and GET requests identical to a regular browser visit.' }
    ]
  },
  'schema-markup-generator': {
    badge: 'Search Snippets',
    users: '16,700+ monthly users',
    rating: 4.9,
    mockupType: 'generator',
    previewMetrics: [
      { label: 'Schema Type', val: 'FAQPage & LocalBusiness' },
      { label: 'Format', val: 'Google JSON-LD Valid' },
      { label: 'Rich Results', val: '100% Eligible', highlight: true }
    ],
    pros: [
      'Generates valid Google JSON-LD schema markup for FAQs, Local Business, Articles, and How-To guides',
      'Increases Google search SERP click-through rates by qualifying pages for rich snippet accordions',
      'Zero syntax errors: guarantees balanced brackets, escaped strings, and required Schema.org fields',
      'Visual form inputs turn complex structured data into effortless 1-click copy code'
    ],
    cons: [
      'Google does not guarantee rich snippet display; algorithmic quality guidelines must still be met',
      'Must be pasted into the <head> or <body> HTML of your webpage (or injected via Google Tag Manager)'
    ],
    howToUse: [
      { step: 1, title: 'Choose Schema Markup Type', desc: 'Select from FAQ, Local Business, Article, Product, or How-To schema types.' },
      { step: 2, title: 'Fill In Structured Information', desc: 'Enter your business name, address, FAQs, questions, and authoritative answers into the form.' },
      { step: 3, title: 'Inspect Generated JSON-LD Code', desc: 'The preview panel renders clean, valid <script type="application/ld+json"> code in real time.' },
      { step: 4, title: 'Copy & Paste into Webpage', desc: 'Click Copy Code and paste it directly into your website header or CMS template.' }
    ],
    useCases: [
      'Content publishers creating rich FAQ accordions on cornerstone blog articles',
      'Local service contractors optimizing Google Maps and Local Pack visibility',
      'E-commerce brands marking up product ratings, pricing, and in-stock availability'
    ],
    faqs: [
      { q: 'Where should I put the schema markup on my site?', a: 'Paste the generated <script> code directly into the <head> of your webpage, or inject it using Google Tag Manager (GTM).' }
    ]
  },
  'invoice-generator': {
    badge: 'Client Billing',
    users: '19,800+ monthly users',
    rating: 4.9,
    mockupType: 'generator',
    previewMetrics: [
      { label: 'Invoice #', val: 'INV-2026-0042' },
      { label: 'Line Items', val: 'Design & Development' },
      { label: 'Calculated Total', val: '$4,850.00 Net 30', highlight: true }
    ],
    pros: [
      '100% private client billing: line items, pricing, and client details never leave your computer',
      'Generates crisp, print-ready PDF invoices formatted for US Letter and A4 international standards',
      'Automatic calculation of subtotal, tiered taxes, line item discounts, and outstanding balances',
      'Supports custom company logo upload and local storage auto-save to prevent data loss'
    ],
    cons: [
      'Does not connect to automatic credit card payment processors (Stripe/PayPal links must be added manually in notes)',
      'Stored in browser local storage; clearing browser cache will erase saved client templates'
    ],
    howToUse: [
      { step: 1, title: 'Add Company & Client Details', desc: 'Enter your business name, address, email, and your client billing information.' },
      { step: 2, title: 'Add Line Items & Services', desc: 'Type project deliverables, quantities, and hourly or fixed rates with automatic subtotals.' },
      { step: 3, title: 'Set Tax & Payment Terms', desc: 'Add applicable sales tax percentages, invoice due date, and payment instructions (e.g. Bank Wire or Wise).' },
      { step: 4, title: 'Download PDF or Print', desc: 'Click Download PDF to receive a beautifully styled, professional PDF invoice ready to email to your client.' }
    ],
    useCases: [
      'Freelancers and independent consultants sending monthly service retainer bills',
      'B2B agencies requiring clean, fast invoice generation without paying for FreshBooks/QuickBooks',
      'Contractors invoicing clients on-site directly from a mobile device or laptop'
    ],
    faqs: [
      { q: 'Is my financial data saved on BizAIPro servers?', a: 'No. The entire invoice generation process occurs inside your local browser memory. Zero financial data is ever transmitted or stored.' }
    ]
  },
  'saas-pricing-profit-calculator': {
    badge: 'SaaS Unit Economics',
    users: '16,500+ monthly founders',
    rating: 4.9,
    mockupType: 'calculator',
    previewMetrics: [
      { label: 'Monthly Recurring (MRR)', val: '$17,150.00' },
      { label: 'LTV : CAC Ratio', val: '4.8x (Healthy)', highlight: true },
      { label: 'Gross Margin', val: '91.8%' }
    ],
    pros: [
      'Comprehensive unit economics model: instantly calculates MRR, ARR, churn impact, and CAC payback',
      'Evaluates business viability with industry-standard LTV:CAC benchmarks (Healthy > 3.0x)',
      'Simulates subscriber churn sensitivity and server hosting COGS per user',
      '100% private in-browser math: financial modeling never leaves your device'
    ],
    cons: [
      'Assumes uniform average revenue per user (ARPU) across the selected tier',
      'Long-term cohort retention curves can vary depending on product expansion revenue',
      'Excludes non-standard enterprise bespoke discounting unless modeled as a custom tier'
    ],
    howToUse: [
      { step: 1, title: 'Input Subscription Plan Price', desc: 'Enter your monthly fee per user or seat (e.g. $49/month) or average blended ARPU.' },
      { step: 2, title: 'Set Subscriber Count & Growth', desc: 'Provide your active paying users, projected monthly growth rate, and customer churn percentage.' },
      { step: 3, title: 'Specify COGS & Operating Costs', desc: 'Add cloud server infrastructure cost per user, marketing acquisition budget, and fixed overhead.' },
      { step: 4, title: 'Analyze Valuation & Payback', desc: 'Review your net margins, LTV, CAC payback window in months, and export your unit economics summary.' }
    ],
    useCases: [
      'Bootstrapped Micro-SaaS founders modeling profitability prior to launch',
      'Venture-backed SaaS executives evaluating CAC payback and growth burn rate',
      'Product managers testing new tiered pricing packages and seat expansion metrics'
    ],
    faqs: [
      { q: 'What is a healthy LTV to CAC ratio for SaaS?', a: 'A standard benchmark is 3:1 (LTV:CAC of 3.0x or higher). A ratio below 1.0x means your acquisition is unprofitable, while a ratio above 5.0x often indicates you are underinvesting in marketing.' },
      { q: 'What is a good SaaS churn rate?', a: 'For B2B SaaS, monthly churn between 1% and 2.5% is considered strong. For B2C or self-serve micro-SaaS, monthly churn between 3% and 5% is typical.' }
    ]
  },
  'marketing-roi-roas-calculator': {
    badge: 'Marketing Profit Engine',
    users: '22,400+ media buyers',
    rating: 4.9,
    mockupType: 'calculator',
    previewMetrics: [
      { label: 'Campaign ROAS', val: '3.70x (370%)' },
      { label: 'Net Marketing ROI', val: '129.4%', highlight: true },
      { label: 'Break-Even ROAS', val: '1.54x' }
    ],
    pros: [
      'Goes beyond top-line ROAS by calculating true net profit after product COGS and agency fees',
      'Determines your exact Break-Even ROAS floor to eliminate unprofitable ad spend',
      'Provides actionable Cost Per Acquisition (CPA) and net profit per customer sale',
      'Instant interactive calculation across Meta, Google Ads, TikTok, and Amazon PPC'
    ],
    cons: [
      'Requires accurate attribution data and realistic product cost of goods (COGS) percentages',
      'Does not automatically deduct deferred customer returns or refund processing delays',
      'Single-campaign model; multi-touch blended attribution requires channel-level inputs'
    ],
    howToUse: [
      { step: 1, title: 'Enter Ad Spend & Revenue', desc: 'Input your total advertising spend and the resulting revenue tracked in your analytics platform.' },
      { step: 2, title: 'Account for Product COGS', desc: 'Add your product manufacturing or delivery cost percentage (e.g. 35% of selling price).' },
      { step: 3, title: 'Include Agency & Tooling Fees', desc: 'Enter any software, contractor, or creative fees associated with managing the campaign.' },
      { step: 4, title: 'Review True Marketing ROI', desc: 'Examine both your top-line ROAS multiple and bottom-line Net ROI to evaluate real campaign profitability.' }
    ],
    useCases: [
      'E-commerce media buyers setting bid caps and target ROAS in Meta Advantage+ and Google PMax',
      'Marketing directors calculating true bottom-line profitability across paid acquisition channels',
      'Agencies proving positive net return on investment to skeptical clients'
    ],
    faqs: [
      { q: 'What is the key difference between ROAS and ROI?', a: 'ROAS measures gross revenue generated per dollar spent on ads (Revenue / Ad Spend). ROI measures net profit earned relative to total marketing investment after deducting product costs and overhead.' }
    ]
  },
  'freelance-hourly-rate-calculator': {
    badge: 'Freelance Architect',
    users: '19,100+ freelancers',
    rating: 4.9,
    mockupType: 'calculator',
    previewMetrics: [
      { label: 'Minimum Hourly Rate', val: '$114.58 / hr', highlight: true },
      { label: 'Standard Day Rate', val: '$916.67 / day' },
      { label: 'Annual Target', val: '$110,000.00' }
    ],
    pros: [
      'Accounts for the billable utilization trap: realistically separates paid client work from admin overhead',
      'Builds in taxes, self-employment levies, health insurance, and paid time off (PTO)',
      'Provides equivalent Day Rates and Monthly Retainers for diverse client pricing proposals',
      'Recommends an automated 15% scope-creep cushion to protect profit margins'
    ],
    cons: [
      'Calculates an hourly cost baseline; value-based pricing may justify significantly higher rates',
      'Tax rates vary by jurisdiction and business registration structure (sole proprietorship vs LLC)',
      'Requires honest estimation of non-billable time spent on sales, invoicing, and marketing'
    ],
    howToUse: [
      { step: 1, title: 'Set Desired Take-Home Pay', desc: 'Enter the net personal annual income you want to take home after all taxes and business expenses.' },
      { step: 2, title: 'Factor Taxes & Business Overhead', desc: 'Estimate your combined tax rate (e.g. 25-30%) and annual business expenses (software, gear, insurance).' },
      { step: 3, title: 'Define Billable Working Capacity', desc: 'Specify weeks worked per year (e.g. 48) and billable utilization percentage (typically 50-65%).' },
      { step: 4, title: 'Adopt Your Billing Architecture', desc: 'Use the calculated minimum hourly rate, day rate, and retainer pricing in your client proposals.' }
    ],
    useCases: [
      'Corporate professionals transitioning to independent freelancing or consulting',
      'Experienced contractors auditing whether current hourly rates cover rising living costs and inflation',
      'Creative agencies calculating developer and designer billing capacity for retainer agreements'
    ],
    faqs: [
      { q: 'Why is billable utilization rate important?', a: 'Freelancers rarely bill 40 hours every week. Between client communication, proposals, admin, and marketing, 50% to 65% utilization (20-26 billable hours per week) is the sustainable standard.' }
    ]
  },
  'business-break-even-calculator': {
    badge: 'Break-Even Forecaster',
    users: '15,800+ business owners',
    rating: 4.9,
    mockupType: 'calculator',
    previewMetrics: [
      { label: 'Break-Even Volume', val: '200 units', highlight: true },
      { label: 'Break-Even Revenue', val: '$20,000.00' },
      { label: 'Contribution Margin', val: '$60.00 (60.0%)' }
    ],
    pros: [
      'Pinpoints exact unit volume and gross revenue required to cover 100% of fixed company overhead',
      'Calculates contribution margin and contribution margin ratio per unit sold',
      'Includes target profit modeling: calculates the volume required to hit specific income goals',
      'Measures margin of safety buffer against seasonal sales drops or market downturns'
    ],
    cons: [
      'Assumes constant selling price and variable cost per unit without volume bulk discounts',
      'Fixed costs must be accurately separated from variable costs for reliable outputs',
      'Multi-product businesses should use blended average selling prices and variable margins'
    ],
    howToUse: [
      { step: 1, title: 'Enter Total Fixed Monthly Costs', desc: 'Input recurring expenses that do not change with production volume (rent, salaries, software, insurance).' },
      { step: 2, title: 'Input Selling Price & Variable Cost', desc: 'Enter your unit selling price and the direct cost required to produce or deliver each unit.' },
      { step: 3, title: 'Add Target Profit Goal', desc: 'Optionally set your desired monthly profit to see how many units you need to sell beyond break-even.' },
      { step: 4, title: 'Evaluate Margin of Safety', desc: 'Compare your break-even volume against your expected monthly sales to verify safety buffer.' }
    ],
    useCases: [
      'Entrepreneurs writing business plans and validating commercial feasibility',
      'Retail and e-commerce stores determining minimum pricing structures before product launches',
      'Service businesses calculating minimum client roster size needed to cover office payroll'
    ],
    faqs: [
      { q: 'What is contribution margin?', a: 'Contribution margin is the selling price minus variable cost per unit. It represents the portion of sales revenue that contributes toward paying down fixed overhead.' },
      { q: 'How can a business lower its break-even point?', a: 'You can lower your break-even point by reducing fixed operating overhead, negotiating lower variable unit costs (COGS), or increasing your selling price.' }
    ]
  }
};

/**
 * Fallback generator providing rich, professional metadata for all remaining tools
 */
export function getToolDetails(tool) {
  if (['plagiarism-checker','ai-content-detector','backlink-checker','reverse-image-search'].includes(tool.slug)) {
    const guide=searchToolGuides[tool.slug];
    return {
      badge:'Browser-based review',mockupType:'generator',
      previewMetrics:[{label:'Input',val:'Your supplied material'},{label:'Processing',val:'Local browser'},{label:'Result',val:'Run the tool',highlight:true}],
      pros:['Works without creating an account','Shows observations from your supplied input','Keeps input processing in your browser'],
      cons:[guide.limitation],
      howToUse:[{step:1,title:'Read the method',desc:guide.formula},{step:2,title:'Supply your input',desc:'Use the labelled fields above. Only enter material you are allowed to use.'},{step:3,title:'Inspect the result',desc:'Check the observations against the material you supplied.'},{step:4,title:'Review the limits',desc:'Verify important conclusions independently before publishing or making a decision.'}],
      useCases:[guide.heading],faqs:[{q:'What does this result establish?',a:guide.limitation}]
    };
  }
  if (curatedToolDetails[tool.slug]) {
    return curatedToolDetails[tool.slug];
  }

  const isCalc = tool.category === 'calculators' || tool.slug.includes('calculator') || tool.slug.includes('planner');
  const isNetwork = tool.engine === 'network' || tool.category === 'domain' || tool.category === 'security';
  const isGenerator = tool.slug.includes('generator') || tool.slug.includes('builder') || tool.slug.includes('formatter');

  const metricType = isCalc ? 'Calculated Metric' : isNetwork ? 'Health Status' : 'Output Payload';
  const highlightVal = isCalc ? 'Depends on your inputs' : isNetwork ? 'Run a public check' : 'Review your output';

  return {
    badge: isCalc ? 'Business Calculator' : isNetwork ? 'Network Diagnostics' : 'Productivity Utility',
    mockupType: isCalc ? 'calculator' : isNetwork ? 'scanner' : 'generator',
    previewMetrics: [
      { label: 'Input Parameters', val: 'Custom Business Data' },
      { label: 'Processing Engine', val: tool.processing || 'Local Browser' },
      { label: metricType, val: highlightVal, highlight: true }
    ],
    pros: [
      tool.processing || 'Review the processing method shown above before entering your data',
      `Completely free to use with zero required account creation or recurring subscription fees`,
      `Engineered for business clarity: clean inputs with transparent formulas and outputs`,
      `Browser-based interface with labelled inputs and reviewable output`
    ],
    cons: [
      `Requires accurate data inputs for meaningful strategic business analysis`,
      `Provides mathematical models and technical estimations; not formal legal, tax, or investment advice`,
      `Local results are stored in session memory; save or export important calculations before closing`
    ],
    howToUse: [
      { step: 1, title: 'Enter Your Business Parameters', desc: `Provide your exact project figures or target URL into the input fields above.` },
      { step: 2, title: 'Review Instant Results', desc: `The tool executes automatically or on submit, computing exact mathematical outputs and indicators.` },
      { step: 3, title: 'Adjust Assumptions & Compare', desc: `Tweak numbers or settings to test alternative scenarios and observe sensitivity.` },
      { step: 4, title: 'Export or Apply to Operations', desc: `Copy outputs to your clipboard or download files to integrate into your daily business workflow.` }
    ],
    useCases: [
      `Small business operators seeking fast, accurate answers without expensive software subscriptions`,
      `Growth marketers evaluating campaign numbers and technical website configurations`,
      `Freelancers and agencies delivering data-backed recommendations to clients`
    ],
    faqs: [
      { q: `Where does ${tool.name} process my inputs?`, a: `${tool.processing || 'Read the tool-specific processing note before entering data'}. ${isNetwork ? 'Requests depend on the website and provider availability. Do not submit credentials or private addresses.' : 'Review the tool-specific notes and avoid entering information you are not authorized to use.'}` },
      { q: `Can I use ${tool.name} for client projects?`, a: `Review the output for accuracy and check applicable content, licensing and provider requirements before using it in a client deliverable.` }
    ]
  };
}
